/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import apiClient from './client';
import type { CashSettlementResult } from './payments';
import type { BookingSource } from '@/utils/bookingOrigin';
import {
  APIResponse,
  BookingsDTO,
  BookingCarSegment,
  BookingDTO,
  CardDTO,
  ActivePaymentDTO,
  SmsLogEntry,
  ApproveBookingResponse
} from '@/types';

/** A rental company the signed-in driver keeps a card set with (GET TollPayment/my-card-owners). */
export interface CardOwnerDTO {
  ownerId: string;
  name: string;
}

/**
 * Which of a driver's card sets a card call is about — cards are per (driver, owner). All optional: the API takes
 * the owner from the session on the owner portal; a driver names the owner, a car of that owner, or the
 * registration link (authKey) the add-card page was opened with.
 */
export interface CardSetRef {
  ownerId?: string;
  carId?: string;
  authKey?: string;
}

/** `?ownerId=…&carId=…&authKey=…` for the parts that are set, or '' when none is. */
export function cardSetQuery(set?: CardSetRef | null): string {
  if (!set) return '';
  const parts: string[] = [];
  if (set.ownerId) parts.push(`ownerId=${encodeURIComponent(set.ownerId)}`);
  if (set.carId) parts.push(`carId=${encodeURIComponent(set.carId)}`);
  if (set.authKey) parts.push(`authKey=${encodeURIComponent(set.authKey)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export interface AllBookingsRequest {
  ownerId?: string;
}

// Server-paged bookings envelope (mirrors backend PagedBookingsDTO).
export interface PagedBookings {
  items: BookingsDTO[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface BookingInfoRequest {
  bookingId: string;
  renterId?: string;
  role?: number;
  cancelType?: number;
}

export interface UpdateBookingDTO {
  bookingId: string;
  /**
   * New start of the booking (ISO UTC). Optional — omit to keep the stored start (the Reopen flow only
   * moves the end). A date in the past is accepted: the backend then pulls that period's tolls into the
   * booking.
   */
  dateFrom?: string;
  dateTo: string;
}

/**
 * What a date change would do, fetched BEFORE it is saved (`POST /Booking/date-change-impact`).
 * Pulling an end date back stops the removed days' tolls from being this renter's, so the owner is
 * shown that and can back out.
 */
export interface BookingDateChangeImpact {
  currentDateFrom: string;
  currentDateTo: string;
  newDateFrom: string;
  newDateTo: string;
  conflictingBookingId?: string | null;
  conflictingDateFrom?: string | null;
  conflictingDateTo?: string | null;
  hasConflict: boolean;
  /**
   * The clash is with the same DRIVER's other booking (another car of this owner), not with this car's
   * calendar. One person cannot be out with two of an owner's cars at once, so the save is refused either
   * way — only the wording and the fix differ.
   */
  conflictIsDriver?: boolean;
  tollsLeavingBooking: number;
  tollsMovingToAnotherBooking: number;
  tollsReturningToOwner: number;
  amountLeavingBooking: number;
  gpsTollsLeavingBooking: number;
  paidTollsStayingCount: number;
  paidTollsStayingAmount: number;
  inFlightTollsStayingCount: number;
  // Citations are attributed by date exactly as crossings are, so a window edit moves them too. Absent on
  // an API build older than 3.0.166 — read them as 0 rather than rendering "undefined fines move".
  violationsLeavingBooking?: number;
  violationsMovingToAnotherBooking?: number;
  violationsBecomingUnattributed?: number;
  violationAmountLeavingBooking?: number;
  paidViolationsStayingCount?: number;
  paidViolationsStayingAmount?: number;
  requiresConfirmation: boolean;
}

/** A card saved on the driver's Stripe customer, as offered for a manual booking charge. */
export interface BookingChargeCard {
  id: string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  isActive: boolean;
}

/** What can be collected for a booking, and whether manual collection is allowed at all. */
export interface BookingChargeInfo {
  bookingId: string;
  price: number;
  collected: number;
  outstanding: number;
  cashPaidAmount?: number | null;
  eligible: boolean;
  /** Why not, when `eligible` is false — shown verbatim to the operator. */
  reason?: string | null;
  driverHasCard: boolean;
  cards: BookingChargeCard[];
}

export interface ManualBookingChargeResult {
  success: boolean;
  message?: string;
  amount: number;
  commission: number;
  paymentIntentId?: string | null;
  cardUsed?: string | null;
  /** Per-card decline reasons when nothing cleared. */
  failureDetail?: string | null;
}

/**
 * What cancelling a booking FROM THE START would release, or did release — mirrors the backend
 * BookingTollDetachResult. `skipped*` is the part the operator has to see: tolls that stay put because
 * the money was already taken, which need a refund rather than a cancellation.
 */
export interface BookingTollRelease {
  movedToOtherBooking: number;
  movedToOwner: number;
  detachedAmount: number;
  totalDetached: number;
  gpsTollsDetached: number;
  skippedAlreadyPaid: number;
  skippedAlreadyPaidAmount: number;
  skippedInFlight: number;
}

/**
 * The same for citations. A released fine goes to the rental that really covers its date, or to nobody —
 * and one whose money already moved (charged to the driver, or settled in cash) stays where it is.
 */
export interface BookingViolationRelease {
  movedToOtherBooking: number;
  orphaned: number;
  detachedAmount: number;
  totalDetached: number;
  skippedSettled: number;
  skippedSettledAmount: number;
}

/** What `cancelFromStartImpact` reports — the two kinds of money side by side. */
export interface BookingCancelFromStartImpact {
  tolls: BookingTollRelease | null;
  violations: BookingViolationRelease | null;
}

/** Outcome of `cancelBookingFromStart`. */
export interface BookingCancelFromStartResult {
  success: boolean;
  previousStatus: number;
  released: BookingTollRelease | null;
  violationsReleased: BookingViolationRelease | null;
}

/** Outcome of `restoreBooking` — mirrors the backend BookingRestoreResult. */
export interface BookingRestoreResult {
  success: boolean;
  /** Refusal reason, or a note that the booking came back in a waiting state. */
  message?: string;
  /** Where the booking landed: Approved (6), WaitingForCC (16) or WaitingForAgreement (15). */
  status: number;
  approvalFlags: number;
}

export const bookingsApi = {
  // Get all bookings for owner
  getAllBookings: async (ownerId: string): Promise<BookingsDTO[]> => {
    const response = await apiClient.get<APIResponse<{ bookings: BookingsDTO[] }>>(
      `/Booking/AllBookings/${ownerId}`
    );
    return response.data.result?.bookings || [];
  },

  // Get all bookings for owner with date range and optional renter/car filter (all statuses)
  getOwnerBookings: async (
    dateFrom?: string,
    dateTo?: string,
    userId?: string,
    carId?: string
  ): Promise<BookingsDTO[]> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (userId) params.append('userId', userId);
    if (carId) params.append('carId', carId);
    const response = await apiClient.get<APIResponse<{ bookings: BookingsDTO[] }>>(
      `/Booking/get-owner-bookings?${params.toString()}`
    );
    return response.data.result?.bookings || [];
  },

  // Server-paged owner bookings (LIST/table view): page/pageSize + server search + status filter.
  // The scheduler view keeps using getOwnerBookings (whole date range).
  getOwnerBookingsPaged: async (
    opts: {
      dateFrom?: string;
      dateTo?: string;
      userId?: string;
      carId?: string;
      page?: number;
      pageSize?: number;
      search?: string;
      statuses?: number[];
      /** Origin codes (utils/bookingOrigin) — which system the booking was created through. */
      origins?: string[];
    }
  ): Promise<PagedBookings> => {
    const pageSize = opts.pageSize ?? 25;
    const params = new URLSearchParams();
    if (opts.dateFrom) params.append('dateFrom', opts.dateFrom);
    if (opts.dateTo) params.append('dateTo', opts.dateTo);
    if (opts.userId) params.append('userId', opts.userId);
    if (opts.carId) params.append('carId', opts.carId);
    if (opts.page) params.append('page', String(opts.page));
    if (opts.pageSize) params.append('pageSize', String(opts.pageSize));
    if (opts.search?.trim()) params.append('search', opts.search.trim());
    if (opts.statuses && opts.statuses.length > 0) params.append('statuses', opts.statuses.join(','));
    if (opts.origins && opts.origins.length > 0) params.append('origins', opts.origins.join(','));
    const response = await apiClient.get<APIResponse<PagedBookings>>(
      `/Booking/get-owner-bookings-paged?${params.toString()}`
    );
    return response.data.result || { items: [], totalCount: 0, page: opts.page ?? 1, pageSize };
  },

  /**
   * Sources the owner's bookings actually came in through — {code, name} for the list's source filter.
   * Derived server-side from the bookings themselves, so a booking plugin added later appears here on
   * its own; never hardcode this list on the client.
   */
  getBookingSources: async (): Promise<BookingSource[]> => {
    const response = await apiClient.get<APIResponse<BookingSource[]>>('/Booking/booking-sources');
    return response.data.result || [];
  },

  // Get booking by ID
  getBooking: async (bookingId: string): Promise<BookingsDTO | null> => {
    const response = await apiClient.get<APIResponse<BookingsDTO>>(
      `/Booking/get-by-id/${bookingId}`
    );
    return response.data.result || null;
  },

  // Get active booking by renter ID (same as WPF: GET /Booking/GetActiveByRenterId/{renterId})
  getActiveBookingByRenterId: async (renterId: string): Promise<BookingDTO | null> => {
    try {
      const response = await apiClient.get<APIResponse<BookingDTO>>(
        `/Booking/GetActiveByRenterId/${renterId}`
      );
      return response.data.result || null;
    } catch {
      return null;
    }
  },

  // Get all bookings for a specific car (same as WPF: GET /Booking/allbookings/{carId}/{rentalType})
  getBookingsOfCar: async (carId: string, rentalType: number = 2): Promise<BookingsDTO[]> => {
    try {
      const response = await apiClient.get<APIResponse<{ bookings: BookingsDTO[] }>>(
        `/Booking/allbookings/${carId}/${rentalType}`
      );
      return response.data.result?.bookings || [];
    } catch {
      return [];
    }
  },

  // Bookings whose rental actually ran (the car drove) — any rental type.
  // For the GPS trip viewer: includes CompletedEarly, not filtered by rental type.
  getDrivenBookingsOfCar: async (carId: string): Promise<BookingsDTO[]> => {
    try {
      const response = await apiClient.get<APIResponse<{ bookings: BookingsDTO[] }>>(
        `/Booking/car-driven-bookings/${carId}`
      );
      return response.data.result?.bookings || [];
    } catch {
      return [];
    }
  },

  // Lightweight per-car availability probe for the vehicle context menu: when a self-billing booking plugin
  // (e.g. HQ) collects this owner's bookings, the server verifies the car still exists in the plugin (one ~0.3s
  // call) and marks it unavailable if not. Fails OPEN (available=true) on any error so the menu stays usable.
  getPluginCarAvailability: async (
    carId: string
  ): Promise<{ available: boolean; reason?: string | null; marked?: boolean }> => {
    try {
      const response = await apiClient.get<APIResponse<{ available: boolean; reason?: string | null; marked?: boolean }>>(
        `/Booking/plugin-car-availability/${carId}`
      );
      return response.data.result ?? { available: true };
    } catch {
      return { available: true };
    }
  },

  // Explicitly mark a car unavailable (unpublish + status 'not') — called by the "Mark as unavailable" button.
  markCarUnavailable: async (carId: string): Promise<boolean> => {
    try {
      const response = await apiClient.post<APIResponse<{ success: boolean }>>(
        `/Booking/mark-car-unavailable/${carId}`
      );
      return response.data.result?.success ?? false;
    } catch {
      return false;
    }
  },

  // Create booking
  createBooking: async (booking: unknown): Promise<APIResponse<BookingDTO>> => {
    const response = await apiClient.post<APIResponse<BookingDTO>>(
      '/Booking',
      booking
    );
    return response.data;
  },

  /**
   * Dry-run of a date change: which reservation it would collide with, and which tolls would leave the
   * booking. Read-only. Returns null when the endpoint is unavailable (an older API), so the caller can
   * fall back to saving straight away instead of blocking the edit.
   */
  getDateChangeImpact: async (booking: UpdateBookingDTO): Promise<BookingDateChangeImpact | null> => {
    try {
      const response = await apiClient.post<APIResponse<BookingDateChangeImpact>>(
        '/Booking/date-change-impact',
        booking
      );
      return response.data.reason === 0 ? response.data.result ?? null : null;
    } catch {
      return null;
    }
  },

  // Update booking status
  updateBookingStatus: async (bookingId: string, status: number): Promise<APIResponse<unknown>> => {
    const response = await apiClient.post<APIResponse<unknown>>(
      `/Booking/update-status?bookingId=${encodeURIComponent(bookingId)}&status=${status}`
    );
    return response.data;
  },

  /** Outstanding rental, the driver's cards, and whether we may collect this booking manually. */
  getChargeInfo: async (bookingId: string): Promise<BookingChargeInfo | null> => {
    const response = await apiClient.get<APIResponse<BookingChargeInfo>>(
      `/Booking/${encodeURIComponent(bookingId)}/charge-info`
    );
    return response.data.result || null;
  },

  /**
   * Charge the outstanding rental to the driver's card now. `cardId` pins the card tried first — that is how a
   * card the driver has just added gets used. Works for bookings whose dates are in the past.
   */
  chargeBookingByCard: async (
    bookingId: string,
    body: { amount: number; cardId?: string }
  ): Promise<ManualBookingChargeResult> => {
    const response = await apiClient.post<APIResponse<ManualBookingChargeResult>>(
      `/Booking/${encodeURIComponent(bookingId)}/charge-card`,
      body
    );
    // A refusal arrives as HTTP 200 with the reason on `message` (the usual envelope here).
    const result = response.data.result;
    if (result) return result;
    return { success: false, message: response.data.message || undefined, amount: 0, commission: 0 };
  },

  /** Record the rental as settled by hand for the amount actually received. */
  chargeBookingByCash: async (
    bookingId: string,
    body: { cashAmount: number; method?: string; note?: string }
  ): Promise<CashSettlementResult> => {
    const response = await apiClient.post<APIResponse<CashSettlementResult>>(
      `/Booking/${encodeURIComponent(bookingId)}/charge-cash`,
      body
    );
    const result = response.data.result;
    if (result) return result;
    return { success: false, message: response.data.message || undefined } as CashSettlementResult;
  },

  // Update booking date
  updateBooking: async (booking: UpdateBookingDTO): Promise<APIResponse<BookingDTO>> => {
    const response = await apiClient.put<APIResponse<BookingDTO>>(
      '/Booking/update-booking-date-to',
      booking
    );
    return response.data;
  },

  /**
   * Every car this rental was driven on, oldest first (`GET /Booking/car-history/{id}`). A booking row
   * names only the car it carries today, so this is the only way the panel can show the one a mid-rental
   * change swapped off — including the half that change left in a separate booking row.
   *
   * Never throws: the list is decoration on top of a modal that must still open. An older backend has no
   * such route, and an empty array simply falls back to the booking's own car.
   */
  getBookingCarHistory: async (bookingId: string): Promise<BookingCarSegment[]> => {
    try {
      const response = await apiClient.get<APIResponse<BookingCarSegment[]>>(
        `/Booking/car-history/${encodeURIComponent(bookingId)}`
      );
      return response.data.result || [];
    } catch {
      return [];
    }
  },

  // Change the car assigned to a booking
  changeBookingCar: async (bookingId: string, carId: string): Promise<APIResponse<boolean>> => {
    const response = await apiClient.put<APIResponse<boolean>>(
      '/Booking/change-car',
      { bookingId, carId }
    );
    return response.data;
  },

  // Complete booking (set IsCompleted flag)
  completeBooking: async (bookingId: string): Promise<APIResponse<boolean>> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      '/Booking/eventbooking',
      { bookingId, status: 7, command: true }
    );
    return response.data;
  },

  // Cancel booking (role: 0=Renter, 1=Owner, 2=Employee)
  cancelBooking: async (bookingId: string, role: number = 1): Promise<APIResponse<null>> => {
    const response = await apiClient.post<APIResponse<null>>(
      '/booking/cancel',
      { bookingId, role, cancelType: 0 }
    );
    return response.data;
  },

  // Undo a cancellation. The booking does NOT come back on the status it had — the backend re-evaluates the
  // approval flags, so a driver who has since added a card lands on Approved while one who still has none
  // lands back on WaitingForCC. A refusal (car re-let in the meantime) arrives as HTTP 200 with
  // success=false and the reason on message, so read the flag rather than the status code.
  restoreBooking: async (bookingId: string): Promise<BookingRestoreResult> => {
    const response = await apiClient.post<APIResponse<BookingRestoreResult>>(
      '/Booking/restore',
      { bookingId }
    );
    const result = response.data.result;
    if (result) return result;
    return { success: false, message: response.data.message || undefined, status: 0, approvalFlags: 0 };
  },

  // What cancelling this booking from the start would release. Read-only — shown before the operator
  // confirms, because the release re-points money for a period that has already happened.
  cancelFromStartImpact: async (bookingId: string): Promise<BookingCancelFromStartImpact | null> => {
    try {
      const response = await apiClient.post<APIResponse<BookingCancelFromStartImpact>>(
        '/Booking/cancel-from-start-impact',
        { bookingId }
      );
      return response.data.reason === 0 ? response.data.result ?? null : null;
    } catch {
      return null;
    }
  },

  // Cancel a booking from the start: the rental is declared never to have happened, and every unpaid toll
  // on it goes back to whoever really made it (another booking of the car, or the owner). Works while the
  // booking runs and after it has finished.
  cancelBookingFromStart: async (bookingId: string): Promise<APIResponse<BookingCancelFromStartResult>> => {
    const response = await apiClient.post<APIResponse<BookingCancelFromStartResult>>(
      '/Booking/cancel-from-start',
      { bookingId }
    );
    return response.data;
  },

  // Approve booking (role: 0=Renter, 1=Owner, 2=Employee)
  // platform: 0=Stripe, 1=Checkbook, 2=Cash (WPF uses Cash=2)
  // platform: 0 = Stripe (we charge — default), 2 = Cash. When WE collect the booking it defaults to Stripe
  // and is charged once the renter's card is present/added; pass useCash=true to settle it as Cash instead.
  // The backend still forces Cash when the owner collects via a booking plugin, regardless of this value.
  // Refusals arrive as reason=0 with result.isSuccess=false — always run the answer through
  // getApproveBookingError() instead of assuming a 200 means the booking moved.
  approveBooking: async (bookingId: string, renterId: string, role: number = 1, useCash: boolean = false): Promise<APIResponse<ApproveBookingResponse>> => {
    const response = await apiClient.post<APIResponse<ApproveBookingResponse>>(
      '/Booking/approve',
      { bookingId, renterId, role, platform: useCash ? 2 : 0, cancelType: 0 }
    );
    return response.data;
  },

  // Insurance approval booking (role: 0=Renter, 1=Owner, 2=Employee).
  // Same trap as approve: a refusal is reason=0 with result=false.
  insuranceApproval: async (bookingId: string, role: number = 1): Promise<APIResponse<boolean>> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      '/Booking/insuranceapproval',
      { bookingId, role }
    );
    return response.data;
  },

  // Create agreement document and send email
  createAgreement: async (data: {
    userId: string;
    bookingId: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    companyName?: string;
    email: string;
    phoneNumber: string;
    state: string;
    address: string;
    vehicleType: string;
    yearColorLicense: string;
    makeAndModel: string;
    dateOfBirth: string;
    startDate: string;
    endDate: string;
    dueDate: string;
    vin: string;
    ratePerDay: number;
    securityDeposit: number;
    totalCharges: number;
    vehicleStateTax: number;
    finalTotal: number;
    last4Digits?: string;
    expirationDate?: string;
    paymentMethodId?: string;
    // Fields matching WPF SendAgreementAsync
    insuranceCompany?: string;
    policyNumber?: string;
    ownerId?: string;
  }): Promise<APIResponse<boolean>> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      '/Agreement/create-agreement-document',
      data
    );
    return response.data;
  },

  // Simplified booking flow - creates booking, approves, insurance approval, and sends single email with contract
  createCompleteBooking: async (data: {
    // Booking data
    carId: string;
    dateFrom: string;
    dateTo: string;
    renterId: string;
    carTypeId: string;

    // Agreement data
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    state: string;
    address: string;
    vehicleType: string;
    yearColorLicense: string;
    makeAndModel: string;
    dateOfBirth: string;
    vin: string;
    ratePerDay: number;
    securityDeposit: number;
    totalCharges: number;
    vehicleStateTax: number;
    finalTotal: number;
    last4Digits?: string;
    expirationDate?: string;
    paymentMethodId?: string;
    // When WE collect the booking, it defaults to Stripe (charged once the renter's card is present or
    // added). Set true to explicitly create it as a Cash booking (no charge / no deposit hold).
    useCash?: boolean;
  }): Promise<APIResponse<{ bookingId: string; message: string }>> => {
    const response = await apiClient.post<APIResponse<{ bookingId: string; message: string }>>(
      '/Booking/create-complete-booking',
      data
    );
    return response.data;
  },

  // Get checkout session URL (uses existing encrypted endpoint, decrypts client-side like WPF)
  // `set` names WHICH of the driver's card sets (cards are per driver and owner) — optional: the owner portal's
  // session already implies it; a driver acting for themselves names it (ownerId or carId).
  getCheckoutSessionUrl: async (userId: string, set?: CardSetRef): Promise<{ url: string | null; error?: string }> => {
    try {
      const { aesDecrypt } = await import('@/utils/aes');
      const response = await apiClient.get<APIResponse<{ hash: string }>>(
        `/tollPayment/get-checkout-session-url-byId/${userId}${cardSetQuery({ ownerId: set?.ownerId, carId: set?.carId })}`
      );

      const data = response.data as { result?: { hash: string }; error?: string; message?: string };
      if (data.error) {
        return { url: null, error: data.message || data.error };
      }

      const hash = data.result?.hash;
      if (!hash) return { url: null, error: 'No checkout URL returned' };

      const url = aesDecrypt(hash, userId);
      return { url };
    } catch (error: unknown) {
      console.error('Failed to get checkout URL:', error);
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      return { url: null, error: axiosError.response?.data?.message || 'Failed to get checkout URL' };
    }
  },

  // Wizard: update user with real data + create Stripe checkout session
  wizardCheckoutSession: async (data: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    successUrl?: string;
    cancelUrl?: string;
    /** The registration link the page was opened with — the API's proof it acts for userId when there is no session. */
    authKey?: string;
    /** The owner whose card set the card goes into (cards are per driver and owner). */
    ownerId?: string;
  }): Promise<{ url: string | null; external?: boolean; error?: string }> => {
    try {
      const response = await apiClient.post<APIResponse<{ url: string; external?: boolean }>>(
        '/tollPayment/wizard-checkout-session',
        data
      );
      const result = response.data as { result?: { url: string; external?: boolean }; error?: string; message?: string };
      if (result.error) {
        return { url: null, error: result.message || result.error };
      }
      // external=true ⇒ the URL is the plugin's (e.g. HQ) hosted card page; the card lands on the plugin, not our Stripe.
      return { url: result.result?.url || null, external: result.result?.external };
    } catch (error: unknown) {
      console.error('Failed to create wizard checkout session:', error);
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      const errorMessage = axiosError.response?.data?.message ||
                          axiosError.response?.data?.error ||
                          'Failed to create checkout session';
      return { url: null, error: errorMessage };
    }
  },

  // Create a Stripe SetupIntent for inline card collection (returns clientSecret + publishableKey)
  createSetupIntent: async (userId: string): Promise<{ clientSecret: string | null; publishableKey: string | null; error?: string }> => {
    try {
      const response = await apiClient.get<APIResponse<{ clientSecret: string; publishableKey: string }>>(
        `/tollPayment/create-setup-intent/${userId}`
      );
      const data = response.data as { result?: { clientSecret: string; publishableKey: string }; error?: string; message?: string };
      if (data.error) {
        return { clientSecret: null, publishableKey: null, error: data.message || data.error };
      }
      return {
        clientSecret: data.result?.clientSecret || null,
        publishableKey: data.result?.publishableKey || null,
      };
    } catch (error: unknown) {
      console.error('Failed to create setup intent:', error);
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      return {
        clientSecret: null,
        publishableKey: null,
        error: axiosError.response?.data?.message || 'Failed to create setup intent',
      };
    }
  },

  // Confirm SetupIntent and save card as active
  confirmSetupIntent: async (data: { userId: string; paymentMethodId: string }): Promise<{ success: boolean; card?: CardDTO; error?: string }> => {
    try {
      const response = await apiClient.post<APIResponse<{ success: boolean; card?: CardDTO; error?: string }>>(
        '/tollPayment/confirm-setup-intent',
        data
      );
      const result = response.data as { result?: { success: boolean; card?: CardDTO; error?: string } };
      return result.result || { success: false, error: 'No result from server' };
    } catch (error: unknown) {
      console.error('Failed to confirm setup intent:', error);
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      return {
        success: false,
        error: axiosError.response?.data?.message || 'Failed to save card',
      };
    }
  },

  // Get owner's cards (current user)
  getOwnerCards: async (): Promise<{ cards: CardDTO[]; error?: string }> => {
    try {
      const response = await apiClient.get('/TollPayment/get-cards');
      console.log('getOwnerCards raw response:', JSON.stringify(response.data, null, 2));
      
      const data = response.data;
      
      // Check for Stripe error
      if (data.result?.httpStatusCode && data.result.httpStatusCode !== 200) {
        const errorMsg = data.result.stripeError?.message || data.result.message || 'Stripe API error';
        console.error('Stripe API error in getOwnerCards:', errorMsg);
        return { cards: [], error: errorMsg };
      }
      
      // Try different response structures
      // 1. Direct array in result
      let result = data.result || data.Result;
      
      // 2. If result is object, look for cards array inside
      if (result && !Array.isArray(result)) {
        console.log('result is object, keys:', Object.keys(result));
        // Check for common property names
        result = result.cards || result.Cards || result.items || result.Items || 
                 result.data || result.Data || result.list || result.List;
      }
      
      // 3. Maybe it's directly in response
      if (!result) {
        result = data.cards || data.Cards || data.items || data.Items;
      }
      
      if (Array.isArray(result)) {
        return { cards: result as CardDTO[] };
      }
      
      // If result is still an object, maybe the cards are the values
      if (result && typeof result === 'object') {
        const values = Object.values(result);
        if (values.length > 0 && typeof values[0] === 'object') {
          return { cards: values as CardDTO[] };
        }
      }
      
      console.warn('getOwnerCards: could not find cards array in response');
      return { cards: [] };
    } catch (error) {
      console.error('getOwnerCards error:', error);
      return { cards: [], error: 'Failed to load cards' };
    }
  },

  /**
   * The rental companies the signed-in DRIVER keeps (or may start) a card set with — the driver's own card screens
   * show cards per company. Any failure answers an empty list.
   */
  getMyCardOwners: async (): Promise<{ owners: CardOwnerDTO[]; error?: string }> => {
    try {
      const response = await apiClient.get('/TollPayment/my-card-owners');
      const data = response.data;
      const result = data?.result ?? data?.Result;
      if (!Array.isArray(result)) return { owners: [], error: data?.message || undefined };
      const owners = result
        .map((o: { ownerId?: string; OwnerId?: string; name?: string; Name?: string }) => ({
          ownerId: o.ownerId ?? o.OwnerId ?? '',
          name: o.name ?? o.Name ?? '',
        }))
        .filter((o: CardOwnerDTO) => !!o.ownerId);
      return { owners };
    } catch (error) {
      console.error('getMyCardOwners error:', error);
      return { owners: [], error: 'Failed to load rental companies' };
    }
  },

  // Get cards by user ID (for renter). `set` names WHICH of the driver's card sets (cards are per driver and
  // owner): optional on the owner portal (the session implies the owner); a driver names the owner, the car, or
  // the registration link (authKey) the page was opened with.
  getRenterCards: async (userId: string, set?: CardSetRef): Promise<{ cards: CardDTO[]; error?: string }> => {
    try {
      const response = await apiClient.get(`/TollPayment/get-cards-byId/${userId}${cardSetQuery(set)}`);
      console.log('getRenterCards raw response:', response.data);
      
      const data = response.data;
      
      // Check for Stripe error
      if (data.result?.httpStatusCode && data.result.httpStatusCode !== 200) {
        const errorMsg = data.result.stripeError?.message || data.result.message || 'Stripe API error';
        return { cards: [], error: errorMsg };
      }
      
      let result = data.result || data.Result;
      
      if (result && !Array.isArray(result)) {
        result = result.cards || result.Cards || result.items || result.Items ||
                 result.data || result.Data || result.list || result.List;
      }
      
      if (!result) {
        result = data.cards || data.Cards || data.items || data.Items;
      }
      
      if (Array.isArray(result)) {
        return { cards: result as CardDTO[] };
      }
      
      console.warn('getRenterCards: could not find cards array in response', data);
      return { cards: [] };
    } catch (error) {
      console.error('getRenterCards error:', error);
      // Keep the API's reason (e.g. "Choose the rental companyâ€¦") â€” it tells the user what to do.
      const axiosError = error as { response?: { data?: { message?: string } } };
      return { cards: [], error: axiosError.response?.data?.message || 'Failed to load cards' };
    }
  },

  // Set active card (encryption happens on proxy server)
  setActiveCard: async (paymentData: ActivePaymentDTO, ownerId: string): Promise<boolean> => {
    try {
      // Send plain data to proxy - proxy will encrypt before forwarding to .NET API
      const response = await apiClient.post<APIResponse<boolean>>(
        '/tollPayment/set-active-card-proxy',
        { paymentData, ownerId }
      );
      return response.data.result;
    } catch (error) {
      console.error('setActiveCard error:', error);
      return false;
    }
  },

  // Remove card. `ownerId` names the driver's card set (cards are per driver and owner); optional.
  removeCard: async (paymentMethodId: string, ownerId?: string): Promise<boolean> => {
    // Bare JSON string body — axios sets no content type for a string, and the browser's text/plain
    // fallback makes the API answer 415. See authApi.checkEmailAvailable.
    const response = await apiClient.post<APIResponse<boolean>>(
      `/tollPayment/delete-payment-method-card${cardSetQuery({ ownerId })}`,
      JSON.stringify(paymentMethodId),
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data.result;
  },

  // Update VerraToll invoice status by booking ID
  // InvoiceStatus enum: New=0, InProgress=1, Success=2, Failed=3, PendingBlocking=4,
  // ManuallyPaid=5, RegistrationFee=6, CancelledRegistrationFee=8
  updateInvoiceStatusByBookingId: async (bookingId: string, invoiceStatus: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.put<[boolean, string]>(
        '/tollPayment/update-invoice-status-by-bookingId',
        { bookingId, invoiceStatus }
      );
      // API returns Tuple<bool, string> as [bool, string]
      const [success, message] = response.data;
      return { success, error: success ? undefined : message };
    } catch (error) {
      console.error('updateInvoiceStatusByBookingId error:', error);
      return { success: false, error: 'Failed to update invoice status' };
    }
  },

  // Create a registration link with temporary auth token
  createRegistrationLink: async (data: {
    carId: string;
    ownerId: string;
    driverEmail: string;
    driverPhone: string;
    plateNumber: string;
    plateState: string;
    dateFrom: string;
    dateTo: string;
    userId?: string;
    bookingId?: string;
    // When issued from a Turo/P2P pending booking: binds the link so registration commits that
    // pending row instead of creating a duplicate native booking.
    pendingBookingId?: string;
  }): Promise<{ authKey: string; expired: string; registrationUrl?: string; deliveryMethod?: string; emailSent?: boolean; smsSent?: boolean } | null> => {
    try {
      const response = await apiClient.post<APIResponse<{ registrationLink: { authKey: string; expired: string }; registrationUrl: string; deliveryMethod: string; emailSent: boolean; smsSent: boolean }>>(
        '/RegistrationLinkRequest/create',
        data
      );
      const result = response.data.result;
      if (result?.registrationLink) {
        return { ...result.registrationLink, registrationUrl: result.registrationUrl, deliveryMethod: result.deliveryMethod, emailSent: result.emailSent, smsSent: result.smsSent };
      }
      return response.data.result as any || null;
    } catch (error) {
      // Re-throw so callers can surface the real backend message (error.response.data.message)
      // instead of a generic "failed" toast. Swallowing to null here hid the actual cause
      // (e.g. a required field rejected by ModelState). All callers guard with try/catch or .catch.
      console.error('Error creating registration link:', error);
      throw error;
    }
  },

  // Re-invite the driver of an existing booking, resuming wherever onboarding stalled.
  // Unlike createRegistrationLink (new drivers only) this binds the link to the existing
  // driver + booking, so the wizard updates them instead of creating duplicates.
  resumeRegistrationForBooking: async (bookingId: string): Promise<{
    registrationUrl: string | null;
    deliveryMethod?: string;
    emailSent?: boolean;
    smsSent?: boolean;
    outstandingSteps: string[];
    alreadyComplete: boolean;
  }> => {
    const response = await apiClient.post<APIResponse<{
      registrationUrl: string | null;
      deliveryMethod?: string;
      emailSent?: boolean;
      smsSent?: boolean;
      outstandingSteps: string[];
      alreadyComplete: boolean;
    }>>(`/RegistrationLinkRequest/resume-for-booking/${bookingId}`);
    return response.data.result;
  },

  // Send invitation email via backend
  sendInvitationEmail: async (data: {
    email: string;
    invitationUrl: string;
    carTitle?: string;
    plateNumber?: string;
    dateFrom?: string;
    dateTo?: string;
    invitationType?: 'driver' | 'partner';
  }): Promise<boolean> => {
    try {
      await apiClient.post('/Booking/send-invitation-email', data);
      return true;
    } catch (error) {
      console.error('Error sending invitation email:', error);
      return false;
    }
  },

  // Send SMS via Twilio (server-side)
  sendSms: async (
    phoneNumber: string,
    message: string,
    context?: { messageType?: string; bookingId?: string; carId?: string; renterId?: string }
  ): Promise<boolean> => {
    try {
      const response = await apiClient.post<APIResponse<boolean>>(
        '/RegistrationLinkRequest/send-sms',
        { phoneNumber, message, ...context }
      );
      return response.data.result === true;
    } catch (error) {
      console.error('Error sending SMS:', error);
      return false;
    }
  },

  // Log SMS or QR code delivery event
  logSmsEvent: async (data: {
    deliveryType: 'Sms' | 'QrCode';
    messageType: string;
    phoneNumber?: string;
    bookingId?: string;
    ownerId?: string;
    carId?: string;
    renterId?: string;
    success: boolean;
    errorMessage?: string;
    url?: string;
  }): Promise<void> => {
    try {
      await apiClient.post('/RegistrationLinkRequest/log-sms-event', data);
    } catch (error) {
      console.error('Error logging SMS event:', error);
    }
  },

  // Get SMS/QR delivery logs for an owner or renter (server-paged).
  getSmsLogs: async (ownerId: string, params?: {
    dateFrom?: string;
    dateTo?: string;
    deliveryType?: string;
    search?: string;
    renterId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: SmsLogEntry[]; totalCount: number; page: number; pageSize: number }> => {
    const pageSize = params?.pageSize ?? 25;
    try {
      const qp = new URLSearchParams();
      if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
      if (params?.dateTo) qp.append('dateTo', params.dateTo);
      if (params?.deliveryType) qp.append('deliveryType', params.deliveryType);
      if (params?.search) qp.append('search', params.search);
      if (params?.renterId) qp.append('renterId', params.renterId);
      if (params?.page) qp.append('page', String(params.page));
      if (params?.pageSize) qp.append('pageSize', String(params.pageSize));
      const query = qp.toString() ? `?${qp.toString()}` : '';
      const response = await apiClient.get<APIResponse<{ items: SmsLogEntry[]; totalCount: number; page: number; pageSize: number }>>(
        `/RegistrationLinkRequest/get-sms-logs/${ownerId}${query}`
      );
      return response.data.result || { items: [], totalCount: 0, page: params?.page ?? 1, pageSize };
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
      return { items: [], totalCount: 0, page: params?.page ?? 1, pageSize };
    }
  },

  // Validate a registration link auth key (public/anonymous endpoint)
  // Returns link data + JWT token for authenticating subsequent API calls
  getRegistrationLinkByAuthKey: async (authKey: string): Promise<{
    CarId?: string; carId?: string;
    OwnerId?: string; ownerId?: string;
    DriverEmail?: string; driverEmail?: string;
    DriverPhone?: string; driverPhone?: string;
    PlateState?: string; plateState?: string;
    BookingDateFrom?: string; bookingDateFrom?: string;
    BookingDateTo?: string; bookingDateTo?: string;
    UserId?: string; userId?: string;
    BookingId?: string; bookingId?: string;
    Token?: string; token?: string;
    RequireAgreement?: boolean; requireAgreement?: boolean;
    IsCompleted?: boolean; isCompleted?: boolean;
    // Resume flow — driver already exists, and only these steps are still outstanding
    // ("license" | "agreement" | "card"). Absent on a plain new-driver link.
    IsExistingUser?: boolean; isExistingUser?: boolean;
    OutstandingSteps?: string[]; outstandingSteps?: string[];
    // False only when the owner holds the admin-only permission to book a driver with no usable card,
    // in which case onboarding has no card step at all. Absent = a card is required, as before.
    CardRequired?: boolean; cardRequired?: boolean;
    // Vehicle info
    CarBrand?: string; carBrand?: string;
    CarModel?: string; carModel?: string;
    CarYear?: number; carYear?: number;
    CarColor?: string; carColor?: string;
    PlateNumber?: string; plateNumber?: string;
  } | null> => {
    try {
      const response = await apiClient.get(`/RegistrationLinkRequest/get/${authKey}`);
      return response.data.result || null;
    } catch (err: any) {
      // Store last error for debugging on link-expired page
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.errorMessage || err.message;
      (window as any).__lastLinkError = `HTTP ${status}: ${msg}`;
      return null;
    }
  },

  // Update registration link with userId/bookingId after they are created
  updateRegistrationLink: async (authKey: string, data: { userId?: string; bookingId?: string }): Promise<boolean> => {
    try {
      await apiClient.post(`/RegistrationLinkRequest/update-link/${authKey}`, data);
      return true;
    } catch (err) {
      console.warn('Failed to update registration link:', err);
      return false;
    }
  },

  /**
   * Combined endpoint: create booking + set status (15 or 16) + update registration link.
   * Replaces 3 sequential calls (createBooking + updateBookingStatus + updateRegistrationLink).
   */
  createBookingAndFinalize: async (authKey: string, data: {
    userId: string;
    carId: string;
    dateFrom: string;
    dateTo: string;
    requireAgreement: boolean;
  }): Promise<{ bookingId: string | null; carAlreadyBooked: boolean }> => {
    try {
      const response = await apiClient.post<APIResponse<{ bookingId: string | null; carAlreadyBooked: boolean }>>(
        `/RegistrationLinkRequest/create-booking-and-finalize/${authKey}`,
        data
      );
      const result = response.data?.result;
      return {
        bookingId: result?.bookingId || null,
        carAlreadyBooked: result?.carAlreadyBooked || false,
      };
    } catch (err) {
      console.warn('Failed to create booking and finalize:', err);
      return { bookingId: null, carAlreadyBooked: false };
    }
  },
};
